/* eslint-disable */
import {
	faChartArea,
	faChartBar,
	faChartLineUp,
	faChartPyramid,
	faChartScatter,
	faChartScatterBubble,
	faChartSimple,
	faChartSimpleHorizontal,
	faChartTreeMap,
	faCircleDot,
	faCloudWord,
	faDiagramNested,
	faDiagramSankey,
	faFilter,
	faGauge,
	faHeat,
	faLollipop,
	faPieChart,
	faSlider,
	faCompass,
	IconDefinition,
	faChartBullet,
	faChartWaterfall,
	faGripLinesVertical,
	faDumbbell,
	faFilters,
} from '@fortawesome/pro-regular-svg-icons';

export type ChartCategory =
	| 'Popular'
	| 'Basic'
	| 'Comparison'
	| 'Statistical'
	| 'Hierarchical'
	| 'Relationship'
	| 'Gauge & Radial'
	| 'Range';

export type ChartSeriesType = 'single' | 'multi' | 'both';

export interface ChartTypeDefinition {
	type: string;
	label: string;
	icon: IconDefinition;
	category: ChartCategory[];
	description: string;
	seriesType: ChartSeriesType;
	requiresCondition?: string;
}

export const CHART_TYPE_REGISTRY: Record<string, ChartTypeDefinition> = {
	// Single Series
	bar: {
		type: 'bar',
		label: 'Bar',
		icon: faChartSimpleHorizontal,
		category: ['Popular', 'Basic'],
		description: 'Horizontal bar chart',
		seriesType: 'single',
	},
	column: {
		type: 'column',
		label: 'Column',
		icon: faChartSimple,
		category: ['Popular', 'Basic'],
		description: 'Vertical column chart',
		seriesType: 'both',
	},
	line: {
		type: 'line',
		label: 'Line',
		icon: faChartLineUp,
		category: ['Popular', 'Basic'],
		description: 'Line chart',
		seriesType: 'single',
	},
	area: {
		type: 'area',
		label: 'Area',
		icon: faChartArea,
		category: ['Popular', 'Basic'],
		description: 'Area chart',
		seriesType: 'both',
	},
	scatter: {
		type: 'scatter',
		label: 'Scatter',
		icon: faChartScatter,
		category: ['Basic', 'Statistical'],
		description: 'Scatter plot',
		seriesType: 'single',
	},
	pie: {
		type: 'pie',
		label: 'Pie',
		icon: faPieChart,
		category: ['Popular', 'Gauge & Radial'],
		description: 'Pie chart',
		seriesType: 'single',
	},
	donut: {
		type: 'donut',
		label: 'Donut',
		icon: faCircleDot,
		category: ['Popular', 'Gauge & Radial'],
		description: 'Donut chart',
		seriesType: 'single',
	},
	wordcloud: {
		type: 'wordcloud',
		label: 'Word Cloud',
		icon: faCloudWord,
		category: ['Statistical'],
		description: 'Word cloud visualization',
		seriesType: 'single',
	},
	packedbubble: {
		type: 'packedbubble',
		label: 'Packed Bubble',
		icon: faChartScatterBubble,
		category: ['Statistical'],
		description: 'Packed bubble chart',
		seriesType: 'single',
	},
	funnel: {
		type: 'funnel',
		label: 'Funnel',
		icon: faFilter,
		category: ['Basic'],
		description: 'Funnel chart',
		seriesType: 'single',
	},
	lollipop: {
		type: 'lollipop',
		label: 'Lollipop',
		icon: faChartLineUp,
		category: ['Basic'],
		description: 'Lollipop chart',
		seriesType: 'single',
	},
	pyramid: {
		type: 'pyramid',
		label: 'Pyramid',
		icon: faChartPyramid,
		category: ['Gauge & Radial'],
		description: 'Pyramid chart',
		seriesType: 'single',
	},
	odometer: {
		type: 'odometer',
		label: 'Odometer',
		icon: faGauge,
		category: ['Gauge & Radial'],
		description: 'Gauge/Odometer chart',
		seriesType: 'single',
		requiresCondition: 'xAxisArray.length == 1',
	},
	columnrange: {
		type: 'columnrange',
		label: 'Column Range',
		icon: faSlider,
		category: ['Range'],
		description: 'Column range chart',
		seriesType: 'single',
		requiresCondition: 'droppedFacts.length > 1',
	},
	treemapsquarified: {
		type: 'treemapsquarified',
		label: 'Treemap Squarified',
		icon: faChartTreeMap,
		category: ['Hierarchical'],
		description: 'Squarified treemap',
		seriesType: 'single',
	},
	// Category A - New Types
	arearange: {
		type: 'arearange',
		label: 'Area Range',
		icon: faChartArea,
		category: ['Range'],
		description: 'Area chart with range data',
		seriesType: 'both',
	},
	areasplinerange: {
		type: 'areasplinerange',
		label: 'Area Spline Range',
		icon: faChartArea,
		category: ['Range'],
		description: 'Smooth area chart with range data',
		seriesType: 'multi',
	},
	xrange: {
		type: 'xrange',
		label: 'X Range',
		icon: faSlider,
		category: ['Range'],
		description: 'Horizontal range bars',
		seriesType: 'single',
	},
	gauge: {
		type: 'gauge',
		label: 'Gauge',
		icon: faGauge,
		category: ['Gauge & Radial'],
		description: 'Gauge/speedometer chart',
		seriesType: 'single',
		requiresCondition: 'xAxisArray.length == 1',
	},
	variablepie: {
		type: 'variablepie',
		label: 'Variable Pie',
		icon: faPieChart,
		category: ['Gauge & Radial'],
		description: 'Pie chart with varying sizes',
		seriesType: 'single',
	},
	pyramid3d: {
		type: 'pyramid3d',
		label: 'Pyramid 3D',
		icon: faChartPyramid,
		category: ['Gauge & Radial'],
		description: '3D pyramid chart',
		seriesType: 'single',
	},
	columnpyramid: {
		type: 'columnpyramid',
		label: 'Column Pyramid',
		icon: faChartSimple,
		category: ['Basic'],
		description: 'Column chart with pyramid shape',
		seriesType: 'both',
	},
	cylinder: {
		type: 'cylinder',
		label: 'Cylinder',
		icon: faChartSimple,
		category: ['Basic'],
		description: 'Cylinder-shaped column chart',
		seriesType: 'both',
	},
	polar: {
		type: 'polar',
		label: 'Polar',
		icon: faCompass,
		category: ['Statistical'],
		description: 'Polar/spider chart',
		seriesType: 'both',
	},

	// Multi Series
	clusteredcolumn: {
		type: 'clusteredcolumn',
		label: 'Clustered Column',
		icon: faChartSimple,
		category: ['Popular', 'Comparison'],
		description: 'Clustered column chart',
		seriesType: 'multi',
	},
	clusteredbar: {
		type: 'clusteredbar',
		label: 'Clustered Bar',
		icon: faChartSimpleHorizontal,
		category: ['Comparison'],
		description: 'Clustered bar chart',
		seriesType: 'multi',
	},
	clusteredline: {
		type: 'clusteredline',
		label: 'Clustered Line',
		icon: faChartLineUp,
		category: ['Comparison'],
		description: 'Clustered line chart',
		seriesType: 'multi',
	},
	stackedcolumn: {
		type: 'stackedcolumn',
		label: 'Stacked Column',
		icon: faChartSimple,
		category: ['Comparison'],
		description: 'Stacked column chart',
		seriesType: 'multi',
	},
	stackedbar: {
		type: 'stackedbar',
		label: 'Stacked Bar',
		icon: faChartSimpleHorizontal,
		category: ['Comparison'],
		description: 'Stacked bar chart',
		seriesType: 'multi',
	},
	spline: {
		type: 'spline',
		label: 'Spline',
		icon: faChartLineUp,
		category: ['Basic'],
		description: 'Smooth multi-series line chart',
		seriesType: 'multi',
	},
	areaspline: {
		type: 'areaspline',
		label: 'Area Spline',
		icon: faChartArea,
		category: ['Basic'],
		description: 'Smooth area chart',
		seriesType: 'multi',
	},
	heatmap: {
		type: 'heatmap',
		label: 'Heatmap',
		icon: faHeat,
		category: ['Comparison', 'Statistical'],
		description: 'Heatmap visualization',
		seriesType: 'multi',
	},
	sankey: {
		type: 'sankey',
		label: 'Sankey Diagram',
		icon: faDiagramSankey,
		category: ['Hierarchical', 'Relationship'],
		description: 'Sankey diagram',
		seriesType: 'multi',
	},
	dependencywheel: {
		type: 'dependencywheel',
		label: 'Dependency Wheel',
		icon: faDiagramNested,
		category: ['Hierarchical', 'Relationship'],
		description: 'Dependency wheel',
		seriesType: 'multi',
	},
	treemapstripes: {
		type: 'treemapstripes',
		label: 'Treemap Stripes',
		icon: faChartTreeMap,
		category: ['Hierarchical'],
		description: 'Striped treemap',
		seriesType: 'multi',
	},
	bullet: {
		type: 'bullet',
		label: 'Bullet Graph',
		icon: faChartBullet,
		category: ['Comparison'],
		description: 'Compare actual values against targets using bullet charts',
		seriesType: 'single',
	},
	waterfall: {
		type: 'waterfall',
		label: 'Waterfall Chart',
		icon: faChartWaterfall,
		category: ['Comparison'],
		description: 'Displays cumulative effect of sequential positive and negative values',
		seriesType: 'single',
	},
	funnel3d: {
		type: 'funnel3d',
		label: '3D Funnel',
		icon: faFilters,
		category: ['Hierarchical'],
		description: 'Visualize stages in a 3D funnel chart',
		seriesType: 'single',
	},
};

export const CHART_CATEGORIES: ChartCategory[] = [
	'Popular',
	'Basic',
	'Comparison',
	'Statistical',
	'Hierarchical',
	'Relationship',
	'Gauge & Radial',
	'Range',
];

export const DEFAULT_EXPANDED_CATEGORIES: ChartCategory[] = ['Popular', 'Basic', 'Comparison'];

export function getChartTypesByCategory(category: ChartCategory): ChartTypeDefinition[] {
	return Object.values(CHART_TYPE_REGISTRY).filter((def) => def.category.includes(category));
}

export function getChartTypesForSeries(seriesType: 'single' | 'multi'): ChartTypeDefinition[] {
	return Object.values(CHART_TYPE_REGISTRY).filter((def) => {
		if (def.seriesType === 'both') return true;
		return def.seriesType === seriesType;
	});
}

export function getPopularChartTypes(): ChartTypeDefinition[] {
	return Object.values(CHART_TYPE_REGISTRY)
		.filter((def) => def.category.includes('Popular'))
		.slice(0, 8);
}

export function filterChartTypes(query: string): ChartTypeDefinition[] {
	const lowerQuery = query.toLowerCase();
	return Object.values(CHART_TYPE_REGISTRY).filter(
		(def) =>
			def.label.toLowerCase().includes(lowerQuery) ||
			def.description.toLowerCase().includes(lowerQuery) ||
			def.type.toLowerCase().includes(lowerQuery)
	);
}
